/**
 * Text extraction configuration (admin).
 *
 * Four tabs: the active configuration, the list of stored configurations,
 * the provider catalogue and the editor. Every setting carries an info icon
 * whose tooltip explains what it does, the recommended value, the sensible
 * range and what happens when it is raised or lowered; the numbers quoted in
 * those texts were measured on the signed contract scan (see the OCR notes in
 * backend/document-processor/services/text_extraction).
 */

import {
  Add as AddIcon,
  AutoFixHighOutlined as RecommendIcon,
  BugReport as TestIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  CloudQueue as CloudIcon,
  Delete as DeleteIcon,
  DocumentScannerOutlined as ScanIcon,
  InfoOutlined as InfoIcon,
  List as ListIcon,
  MemoryOutlined as ChipIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  SpeedOutlined as SpeedIcon,
  TableChartOutlined as TableIcon,
  TuneOutlined as TuneIcon,
  VerifiedOutlined as QualityIcon,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextExtractionProviderType, TextExtractionRequest } from '@/api/data-contracts';
import {
  useDeleteTextExtractionConfig,
  useGetAllTextExtractionConfigs,
  useGetCurrentTextExtractionConfig,
  useGetTextExtractionProviders,
  useResetTextExtractionConfig,
  useSaveTextExtractionConfig,
  useTestTextExtractionConfig,
} from '@/hooks/api/config/useTextExtractionConfig';
import { AdminTabs, type AdminTab } from '../components/admin';
import { PageLayout } from '../components/ui/PageLayout';
import { useSnackbar } from '../contexts/SnackbarContext';
import { log } from '../utils/errors/logger';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TextExtractionProvider {
  value: string;
  label: string;
  description: string;
  supported_types: string[];
  required_config: string[];
  optional_config: string[];
  features?: string[];
  libraries?: string[];
  is_active?: boolean;
}

/** OCR settings for scanned pages; mirror backend TextExtractionConfig (base.py). */
interface OcrSettings {
  ocr_engine: 'rapidocr' | 'easyocr';
  ocr_language_auto: boolean;
  ocr_page_detection_enabled: boolean;
  ocr_page_min_chars: number;
  ocr_page_min_image_coverage: number;
  ocr_render_scale: number;
  ocr_onnx_device: 'cpu' | 'cuda' | 'auto';
  ocr_max_pages: number;
  ocr_noise_filter_enabled: boolean;
  ocr_deskew_enabled: boolean;
  ocr_deskew_min_angle: number;
  ocr_orientation_enabled: boolean;
  ocr_dictionary_correction_enabled: boolean;
  ocr_vocabulary_path: string;
  ocr_second_pass_enabled: boolean;
  ocr_second_pass_min_p10: number;
}

const OCR_DEFAULTS: OcrSettings = {
  ocr_engine: 'rapidocr',
  ocr_language_auto: true,
  ocr_page_detection_enabled: true,
  ocr_page_min_chars: 64,
  ocr_page_min_image_coverage: 0.3,
  ocr_render_scale: 3.0,
  ocr_onnx_device: 'cpu',
  ocr_max_pages: 150,
  ocr_noise_filter_enabled: true,
  ocr_deskew_enabled: true,
  ocr_deskew_min_angle: 1.0,
  ocr_orientation_enabled: true,
  ocr_dictionary_correction_enabled: true,
  ocr_vocabulary_path: '',
  ocr_second_pass_enabled: true,
  ocr_second_pass_min_p10: 0.6,
};

interface TextExtractionConfigModel extends OcrSettings {
  provider: string;
  timeout: number;
  max_retries: number;
  ocr_enabled: boolean;
  ocr_language: string;
  extract_tables: boolean;
  extract_images: boolean;
  preserve_layout: boolean;
  provider_config: Record<string, unknown>;
  display_name: string;
  description: string;
  is_active?: boolean;
  created_by?: string;
}

// Names the backend seeds on first start. Such configurations are shown with
// localized name and description because the stored strings are English.
const SEEDED_NAMES = new Set(['Local Extraction (Docling)', 'Environment Default (Local)', 'Environment Default']);

// The recommended values, identical to the environment defaults the backend
// seeds; the editor can apply them in one click.
const RECOMMENDED: Omit<TextExtractionConfigModel, 'provider' | 'display_name' | 'description' | 'provider_config'> = {
  ...OCR_DEFAULTS,
  timeout: 60,
  max_retries: 3,
  ocr_enabled: true,
  ocr_language: 'de',
  extract_tables: true,
  extract_images: false,
  preserve_layout: true,
};

interface TestResult {
  success: boolean;
  health: boolean;
  provider_info: Record<string, unknown>;
  supported_types: string[];
  error: string;
  message: string;
}

type BooleanField = {
  [K in keyof TextExtractionConfigModel]-?: NonNullable<TextExtractionConfigModel[K]> extends boolean ? K : never;
}[keyof TextExtractionConfigModel];
type NumberField = {
  [K in keyof TextExtractionConfigModel]-?: NonNullable<TextExtractionConfigModel[K]> extends number ? K : never;
}[keyof TextExtractionConfigModel];

/** Structured guidance shown in a field's tooltip. */
interface Guidance {
  help: string;
  recommended?: string;
  range?: string;
  higher?: string;
  lower?: string;
  on?: string;
  off?: string;
  options?: string;
}

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

/** Info icon with the structured guidance as a tooltip. */
const InfoTip: React.FC<{ title: string; guidance: Guidance }> = ({ title, guidance }) => {
  const { t } = useTranslation();
  const rows: Array<[string, string | undefined]> = [
    [t('textExtractionConfig.guide.recommended'), guidance.recommended],
    [t('textExtractionConfig.guide.range'), guidance.range],
    [t('textExtractionConfig.guide.higher'), guidance.higher],
    [t('textExtractionConfig.guide.lower'), guidance.lower],
    [t('textExtractionConfig.guide.on'), guidance.on],
    [t('textExtractionConfig.guide.off'), guidance.off],
    [t('textExtractionConfig.guide.options'), guidance.options],
  ];
  return (
    <Tooltip
      arrow
      placement="top"
      enterDelay={150}
      slotProps={{ tooltip: { sx: { maxWidth: 380, p: 1.5, fontSize: '0.8rem', lineHeight: 1.45 } } }}
      title={
        <Box>
          <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', mb: 0.5 }}>{title}</Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: rows.some(([, v]) => v) ? 1 : 0 }}>{guidance.help}</Typography>
          {rows.filter(([, v]) => Boolean(v)).map(([label, value]) => (
            <Typography key={label} variant="body2" sx={{ fontSize: '0.78rem', mt: 0.35 }}>
              <Box component="span" sx={{ fontWeight: 600, opacity: 0.85 }}>{label}: </Box>
              {value}
            </Typography>
          ))}
        </Box>
      }
    >
      <InfoIcon
        fontSize="small"
        aria-label={title}
        sx={{ color: 'text.disabled', cursor: 'help', '&:hover': { color: 'primary.main' } }}
      />
    </Tooltip>
  );
};

/** Card with an icon, a title line and an explanatory sentence. */
const SectionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
  tone?: 'default' | 'subtle';
}> = ({ icon, title, description, children, tone = 'default' }) => (
  <Paper
    variant="outlined"
    sx={{
      p: { xs: 2, sm: 3 },
      borderRadius: 3,
      borderColor: 'divider',
      backgroundColor: tone === 'subtle' ? 'action.hover' : 'background.paper',
    }}
  >
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start', mb: description ? 2 : 1.5 }}>
      <Avatar variant="rounded" sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', width: 36, height: 36 }}>
        {icon}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{title}</Typography>
        {description && (
          <Typography variant="body2" color="text.secondary">{description}</Typography>
        )}
      </Box>
    </Stack>
    {children}
  </Paper>
);

/** Key figure tile for the current-configuration overview. */
const StatTile: React.FC<{ label: string; value: React.ReactNode; help: string }> = ({ label, value, help }) => (
  <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2.5, height: '100%' }}>
    <Stack sx={{ alignItems: 'center', justifyContent: 'space-between' }} direction="row" spacing={1}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <InfoTip title={label} guidance={{ help }} />
    </Stack>
    <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.25, wordBreak: 'break-word' }}>{value}</Typography>
  </Paper>
);

/** On/off chip with a tooltip. */
const FeatureChip: React.FC<{ label: string; on: boolean; help: string }> = ({ label, on, help }) => (
  <Tooltip arrow title={help}>
    <Chip
      size="small"
      icon={on ? <CheckIcon /> : <CloseIcon />}
      label={label}
      color={on ? 'success' : 'default'}
      variant={on ? 'filled' : 'outlined'}
      sx={{ fontWeight: 500 }}
    />
  </Tooltip>
);

const FILE_TYPE_CATEGORIES: Array<{ key: 'documents' | 'scans' | 'transcripts' | 'email'; types: string[] }> = [
  { key: 'documents', types: ['pdf', 'docx', 'pptx', 'xlsx', 'html', 'txt'] },
  { key: 'scans', types: ['png', 'jpg', 'jpeg'] },
  { key: 'transcripts', types: ['vtt', 'srt'] },
  { key: 'email', types: ['eml', 'msg'] },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const TextExtractionConfig: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const { data: providers = [], isLoading: isLoadingProviders } = useGetTextExtractionProviders();
  const { data: currentConfig, isLoading: isLoadingCurrent } = useGetCurrentTextExtractionConfig();
  const { data: allConfigs = [], isLoading: isLoadingAll } = useGetAllTextExtractionConfigs();
  const saveConfig = useSaveTextExtractionConfig();
  const testConfig = useTestTextExtractionConfig();
  const deleteConfig = useDeleteTextExtractionConfig();
  const resetConfig = useResetTextExtractionConfig();

  const [activeTab, setActiveTab] = useState(0);
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [config, setConfig] = useState<TextExtractionConfigModel>({
    provider: '',
    timeout: 60,
    max_retries: 3,
    ocr_enabled: true,
    ocr_language: 'de',
    ...OCR_DEFAULTS,
    extract_tables: true,
    extract_images: false,
    preserve_layout: true,
    provider_config: {},
    display_name: '',
    description: '',
  });
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<string | null>(null);

  const isLoading = isLoadingProviders || isLoadingCurrent || isLoadingAll;
  const providerList = providers as TextExtractionProvider[];

  // Seeded configurations and provider ids are shown in the UI language.
  const isSeeded = (cfg: TextExtractionConfigModel) => cfg.created_by === 'system' || SEEDED_NAMES.has(cfg.display_name);
  const configName = (cfg: TextExtractionConfigModel) => (isSeeded(cfg) ? t('textExtractionConfig.defaults.name') : cfg.display_name);
  const configDescription = (cfg: TextExtractionConfigModel) => (isSeeded(cfg) ? t('textExtractionConfig.defaults.description') : cfg.description);
  const providerName = (value: string, fallback?: string) =>
    t(`textExtractionConfig.providers.catalog.${value}.name`, { defaultValue: fallback ?? value });
  const providerShort = (value: string) =>
    t(`textExtractionConfig.providers.catalog.${value}.short`, { defaultValue: value });

  // Guidance lookups (translations carry the structured texts).
  const guide = (key: string): Guidance =>
    t(`textExtractionConfig.${key}`, { returnObjects: true }) as Guidance;

  // Set default provider when providers are loaded
  useEffect(() => {
    if (providerList.length > 0 && !selectedProvider) {
      const localProvider = providerList.find((p) => p.value === 'local');
      handleProviderChange(localProvider ? 'local' : providerList[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per providers load
  }, [providers, selectedProvider]);

  // Set initial config from current config
  useEffect(() => {
    if (currentConfig && !selectedProvider) {
      const currentCfg = currentConfig as TextExtractionConfigModel;
      setSelectedProvider(currentCfg.provider);
      // Older stored configs lack the OCR fields; fill them with the defaults
      // so a save never resets them to undefined on the server.
      setConfig({ ...OCR_DEFAULTS, ...currentCfg, display_name: configName(currentCfg), description: configDescription(currentCfg) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per current-config load; the name helpers only read t
  }, [currentConfig, selectedProvider]);

  const handleProviderChange = (providerValue: string) => {
    setSelectedProvider(providerValue);
    const provider = providerList.find((p) => p.value === providerValue);
    if (provider) {
      setConfig((prev) => ({
        ...prev,
        provider: providerValue,
        display_name: prev.display_name || t('textExtractionConfig.defaults.name'),
        description: prev.description || t('textExtractionConfig.defaults.description'),
        provider_config: {},
      }));
    }
  };

  // The OCR fields are newer than the generated request type (regenerating the
  // client is a separate step); they are sent alongside the typed fields.
  const toTextExtractionRequest = (current: TextExtractionConfigModel): TextExtractionRequest => {
    const ocr: OcrSettings = {
      ocr_engine: current.ocr_engine,
      ocr_language_auto: current.ocr_language_auto,
      ocr_page_detection_enabled: current.ocr_page_detection_enabled,
      ocr_page_min_chars: current.ocr_page_min_chars,
      ocr_page_min_image_coverage: current.ocr_page_min_image_coverage,
      ocr_render_scale: current.ocr_render_scale,
      ocr_onnx_device: current.ocr_onnx_device,
      ocr_max_pages: current.ocr_max_pages,
      ocr_noise_filter_enabled: current.ocr_noise_filter_enabled,
      ocr_deskew_enabled: current.ocr_deskew_enabled,
      ocr_deskew_min_angle: current.ocr_deskew_min_angle,
      ocr_orientation_enabled: current.ocr_orientation_enabled,
      ocr_dictionary_correction_enabled: current.ocr_dictionary_correction_enabled,
      ocr_vocabulary_path: current.ocr_vocabulary_path,
      ocr_second_pass_enabled: current.ocr_second_pass_enabled,
      ocr_second_pass_min_p10: current.ocr_second_pass_min_p10,
    };
    const request: TextExtractionRequest & OcrSettings = {
      provider: current.provider as TextExtractionProviderType,
      timeout: current.timeout,
      max_retries: current.max_retries,
      ocr_enabled: current.ocr_enabled,
      ocr_language: current.ocr_language,
      extract_tables: current.extract_tables,
      extract_images: current.extract_images,
      preserve_layout: current.preserve_layout,
      provider_config: current.provider_config,
      display_name: current.display_name,
      description: current.description,
      ...ocr,
    };
    return request;
  };

  const handleConfigChange = <K extends keyof TextExtractionConfigModel>(
    field: K,
    value: TextExtractionConfigModel[K]
  ) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleProviderConfigChange = (key: string, value: unknown) => {
    setConfig((prev) => ({ ...prev, provider_config: { ...prev.provider_config, [key]: value } }));
  };

  const testConfiguration = async () => {
    setTestResult(null);
    try {
      const result = await testConfig.mutateAsync(config as unknown as Record<string, unknown>);
      setTestResult(result as TestResult);
    } catch (error) {
      log.error('Error testing configuration', { error });
      showError(t('textExtractionConfig.messages.testFailed'));
    }
  };

  const saveConfiguration = async () => {
    try {
      await saveConfig.mutateAsync(toTextExtractionRequest(config));
      showSuccess(t('textExtractionConfig.messages.saveSuccess'));
    } catch (error) {
      log.error('Error saving configuration', { error });
      showError(t('textExtractionConfig.messages.saveFailed'));
    }
  };

  const deleteConfiguration = async (configId: string) => {
    try {
      await deleteConfig.mutateAsync(configId);
      showSuccess(t('textExtractionConfig.messages.deleteSuccess'));
      setDeleteDialogOpen(false);
      setConfigToDelete(null);
    } catch (error) {
      log.error('Error deleting configuration', { error });
      showError(t('textExtractionConfig.messages.deleteFailed'));
    }
  };

  const applyRecommended = () => {
    setConfig((prev) => ({ ...prev, ...RECOMMENDED }));
    showSuccess(t('textExtractionConfig.messages.recommendedApplied'));
  };

  const resetToDefault = async () => {
    try {
      await resetConfig.mutateAsync();
      showSuccess(t('textExtractionConfig.messages.resetSuccess'));
    } catch (error) {
      log.error('Error resetting configuration', { error });
      showError(t('textExtractionConfig.messages.resetFailed'));
    }
  };

  // -------------------------------------------------------------------------
  // Field builders (editor)
  // -------------------------------------------------------------------------

  const tipAdornment = (title: string, guidance: Guidance) => (
    <InputAdornment position="end">
      <InfoTip title={title} guidance={guidance} />
    </InputAdornment>
  );

  const compactHint = (guidance: Guidance): string | undefined => {
    const parts = [
      guidance.recommended ? `${t('textExtractionConfig.guide.recommended')} ${guidance.recommended}` : null,
      guidance.range ?? null,
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : undefined;
  };

  const numberField = (
    field: NumberField,
    label: string,
    guidance: Guidance,
    attrs: { step: number; min: number; max: number }
  ) => (
    <TextField
      fullWidth
      type="number"
      label={label}
      value={config[field]}
      onChange={(e) => handleConfigChange(field, Number(e.target.value))}
      helperText={compactHint(guidance)}
      slotProps={{ htmlInput: attrs, input: { endAdornment: tipAdornment(label, guidance) } }}
    />
  );

  const switchField = (field: BooleanField, label: string, guidance: Guidance) => (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1, borderRadius: 2, height: '100%' }}>
      <Stack sx={{ alignItems: 'center', justifyContent: 'space-between' }} direction="row" spacing={1}>
        <FormControlLabel
          sx={{ mr: 0 }}
          control={
            <Switch checked={config[field]} onChange={(e) => handleConfigChange(field, e.target.checked)} />
          }
          label={<Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>}
        />
        <InfoTip title={label} guidance={guidance} />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 6, pr: 1 }}>
        {config[field] ? guidance.on : guidance.off}
      </Typography>
    </Paper>
  );

  // -------------------------------------------------------------------------
  // Tab: current configuration
  // -------------------------------------------------------------------------

  const renderCurrentConfig = () => {
    const cfg = currentConfig ? ({ ...OCR_DEFAULTS, ...(currentConfig as TextExtractionConfigModel) }) : null;
    if (isLoadingCurrent) {
      return <CircularProgress />;
    }
    if (!cfg) {
      return (
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography>{t('textExtractionConfig.currentConfig.noActiveConfig')}</Typography>
          </CardContent>
        </Card>
      );
    }
    const engineLabel = cfg.ocr_engine === 'easyocr'
      ? t('textExtractionConfig.ocr.engineEasyocr')
      : t('textExtractionConfig.ocr.engineRapidocr');
    const fh = (key: string) => t(`textExtractionConfig.featureHelp.${key}`);
    return (
      <Stack spacing={2.5}>
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack sx={{ alignItems: { sm: 'center' } }} direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Avatar variant="rounded" sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                <ScanIcon />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{configName(cfg)}</Typography>
                  <Chip size="small" color="success" label={t('textExtractionConfig.currentConfig.statusActive')} />
                  <Chip size="small" variant="outlined" label={providerShort(cfg.provider)} />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{configDescription(cfg)}</Typography>
              </Box>
              <Button variant="outlined" startIcon={<TuneIcon />} onClick={() => setActiveTab(3)}>
                {t('textExtractionConfig.actions.configure')}
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {t('textExtractionConfig.currentConfig.editHint')}
            </Typography>
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile label={t('textExtractionConfig.form.timeout')} value={`${cfg.timeout} s`} help={fh('timeout')} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile label={t('textExtractionConfig.form.maxRetries')} value={cfg.max_retries} help={fh('maxRetries')} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile label={t('textExtractionConfig.ocr.engine')} value={cfg.ocr_enabled ? engineLabel.split(' (')[0] : t('textExtractionConfig.features.disabled')} help={fh('engine')} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile label={t('textExtractionConfig.ocr.maxPages')} value={cfg.ocr_max_pages === 0 ? '∞' : cfg.ocr_max_pages} help={fh('budget')} />
          </Grid>
        </Grid>

        <SectionCard icon={<ScanIcon fontSize="small" />} title={t('textExtractionConfig.currentConfig.ocrSummary')} description={t('textExtractionConfig.sections.ocr.description')}>
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <FeatureChip label={t('textExtractionConfig.features.ocr')} on={cfg.ocr_enabled} help={fh('ocr')} />
            <Tooltip arrow title={fh('language')}>
              <Chip size="small" variant="outlined" label={`${t('textExtractionConfig.form.ocrLanguage')}: ${cfg.ocr_language}`} />
            </Tooltip>
            <FeatureChip label={t('textExtractionConfig.ocr.languageAuto')} on={cfg.ocr_language_auto} help={fh('languageAuto')} />
            <FeatureChip label={t('textExtractionConfig.ocr.pageDetection')} on={cfg.ocr_page_detection_enabled} help={fh('scanDetection')} />
            <FeatureChip label={t('textExtractionConfig.ocr.deskew')} on={cfg.ocr_deskew_enabled} help={fh('deskew')} />
            <FeatureChip label={t('textExtractionConfig.ocr.orientation')} on={cfg.ocr_orientation_enabled} help={fh('orientation')} />
            <FeatureChip label={t('textExtractionConfig.ocr.noiseFilter')} on={cfg.ocr_noise_filter_enabled} help={fh('noise')} />
            <FeatureChip label={t('textExtractionConfig.ocr.dictionaryCorrection')} on={cfg.ocr_dictionary_correction_enabled} help={fh('correction')} />
            <FeatureChip label={t('textExtractionConfig.ocr.secondPass')} on={cfg.ocr_second_pass_enabled} help={fh('secondPass')} />
          </Stack>
        </SectionCard>

        <SectionCard icon={<TableIcon fontSize="small" />} title={t('textExtractionConfig.currentConfig.extractionSummary')} description={t('textExtractionConfig.sections.extraction.description')}>
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <FeatureChip label={t('textExtractionConfig.features.tables')} on={cfg.extract_tables} help={fh('tables')} />
            <FeatureChip label={t('textExtractionConfig.features.images')} on={cfg.extract_images} help={fh('images')} />
            <FeatureChip label={t('textExtractionConfig.features.layout')} on={cfg.preserve_layout} help={fh('layout')} />
          </Stack>
        </SectionCard>
      </Stack>
    );
  };

  // -------------------------------------------------------------------------
  // Tab: all configurations
  // -------------------------------------------------------------------------

  const renderAllConfigs = () => (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('textExtractionConfig.messages.allConfigurations')}</Typography>
          <Tooltip arrow title={t('textExtractionConfig.actions.resetHint')}>
            <span>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { void resetToDefault(); }} disabled={resetConfig.isPending}>
                {t('textExtractionConfig.actions.reset')}
              </Button>
            </span>
          </Tooltip>
        </Stack>
        {isLoadingAll ? (
          <CircularProgress />
        ) : Array.isArray(allConfigs) && allConfigs.length > 0 ? (
          <List disablePadding>
            {(allConfigs as TextExtractionConfigModel[]).map((cfg, index) => {
              const isEnvDefault = isSeeded(cfg);
              return (
                <React.Fragment key={index}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem
                    sx={{ py: 1.5, px: 1 }}
                    secondaryAction={
                      isEnvDefault ? (
                        <Tooltip arrow title={t('textExtractionConfig.allConfigs.envDefault')}>
                          <InfoIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                        </Tooltip>
                      ) : (
                        <Tooltip arrow title={t('textExtractionConfig.allConfigs.deleteTooltip')}>
                          <IconButton edge="end" onClick={() => { setConfigToDelete(index.toString()); setDeleteDialogOpen(true); }}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )
                    }
                  >
                    <ListItemAvatar>
                      <Avatar variant="rounded" sx={{ bgcolor: cfg.is_active ? 'success.main' : 'action.selected', color: cfg.is_active ? 'success.contrastText' : 'text.secondary' }}>
                        <SettingsIcon fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                          <Typography sx={{ fontWeight: 600 }}>{configName(cfg)}</Typography>
                          <Chip size="small" variant="outlined" label={providerShort(cfg.provider)} />
                          <Chip
                            size="small"
                            color={cfg.is_active ? 'success' : 'default'}
                            label={cfg.is_active ? t('textExtractionConfig.messages.active') : t('textExtractionConfig.allConfigs.inactive')}
                          />
                        </Stack>
                      }
                      secondary={configDescription(cfg)}
                      slotProps={{ secondary: { sx: { mt: 0.5 } } }}
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary">{t('textExtractionConfig.messages.noConfigurationsFound')}</Typography>
        )}
      </CardContent>
    </Card>
  );

  // -------------------------------------------------------------------------
  // Tab: providers
  // -------------------------------------------------------------------------

  const renderAvailableProviders = () => (
    <Stack spacing={2}>
      {providerList.map((provider) => {
        const types = new Set(provider.supported_types.map((x) => x.toLowerCase()));
        const categories = FILE_TYPE_CATEGORIES
          .map((c) => ({ ...c, present: c.types.filter((x) => types.has(x)) }))
          .filter((c) => c.present.length > 0 || c.key === 'scans');
        const name = providerName(provider.value, provider.label);
        const tagline = t(`textExtractionConfig.providers.catalog.${provider.value}.tagline`, { defaultValue: '' });
        const description = t(`textExtractionConfig.providers.catalog.${provider.value}.description`, { defaultValue: provider.description });
        return (
          <Card key={provider.value} variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'auto minmax(0, 1fr)', md: 'auto minmax(0, 1fr) auto' },
                gap: 2,
                alignItems: 'center',
                p: { xs: 2, sm: 3 },
                pb: { xs: 2, sm: 2.5 },
                borderBottom: 1,
                borderColor: 'divider',
                background: (theme) => `linear-gradient(90deg, ${theme.palette.action.hover}, transparent)`,
              }}
            >
              <Avatar variant="rounded" sx={{ bgcolor: 'primary.main', width: 52, height: 52 }}>
                <CloudIcon />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{name}</Typography>
                  {provider.is_active && <Chip size="small" color="success" label={t('textExtractionConfig.providers.active')} />}
                  <Chip size="small" variant="outlined" icon={<ChipIcon />} label={t('textExtractionConfig.providers.local')} />
                </Stack>
                {tagline && (
                  <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>{tagline}</Typography>
                )}
              </Box>
              <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' }, justifySelf: { xs: 'stretch', md: 'end' } }}>
                <Tooltip arrow title={t('textExtractionConfig.providers.configureHint')}>
                  <Button fullWidth variant="contained" startIcon={<TuneIcon />} onClick={() => { handleProviderChange(provider.value); setActiveTab(3); }}>
                    {t('textExtractionConfig.actions.configure')}
                  </Button>
                </Tooltip>
              </Box>
            </Box>

            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: 900 }}>{description}</Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 5 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.25 }}>{t('textExtractionConfig.providers.columns.types')}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>{t('textExtractionConfig.providers.typesHint')}</Typography>
                  <Stack spacing={1.25}>
                    {categories.map((c) => (
                      <Box key={c.key} sx={{ display: 'grid', gridTemplateColumns: '120px minmax(0, 1fr)', gap: 1, alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {t(`textExtractionConfig.providers.categories.${c.key}`)}
                        </Typography>
                        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                          {c.key === 'scans' && <Chip size="small" label="PDF (OCR)" />}
                          {c.present.map((type) => (
                            <Chip key={type} size="small" label={type.toUpperCase()} />
                          ))}
                          {c.key === 'scans' && (
                            <InfoTip title={t('textExtractionConfig.providers.categories.scans')} guidance={{ help: t('textExtractionConfig.providers.scanNote') }} />
                          )}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.25 }}>{t('textExtractionConfig.providers.columns.features')}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>{t('textExtractionConfig.providers.featuresHint')}</Typography>
                  <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
                    {(provider.features ?? []).map((feature) => {
                      const label = t(`textExtractionConfig.providerFeatures.${feature}.label`, { defaultValue: feature });
                      const help = t(`textExtractionConfig.providerFeatures.${feature}.help`, { defaultValue: '' });
                      return (
                        <Tooltip key={feature} arrow title={help}>
                          <Chip size="small" variant="outlined" color="primary" icon={<CheckIcon />} label={label} />
                        </Tooltip>
                      );
                    })}
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.25 }}>{t('textExtractionConfig.providers.columns.tech')}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>{t('textExtractionConfig.providers.techHint')}</Typography>
                  <Stack spacing={0.75}>
                    {(provider.libraries ?? []).map((lib) => (
                      <Stack key={lib} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <ChipIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                        <Typography variant="body2">{lib}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );

  // -------------------------------------------------------------------------
  // Tab: editor
  // -------------------------------------------------------------------------

  const renderProviderConfigFields = () => {
    const provider = providerList.find((p) => p.value === selectedProvider);
    if (!provider) { return null; }
    const allConfigFields = [...provider.required_config, ...provider.optional_config];
    if (allConfigFields.length === 0) { return null; }
    return (
      <SectionCard icon={<SettingsIcon fontSize="small" />} title={t('textExtractionConfig.sections.providerConfig.title')} description={t('textExtractionConfig.sections.providerConfig.description')}>
        <Grid container spacing={2}>
          {allConfigFields.map((field) => (
            <Grid size={{ xs: 12, sm: 6 }} key={field}>
              <TextField
                fullWidth
                label={field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                value={config.provider_config[field] || ''}
                onChange={(e) => handleProviderConfigChange(field, e.target.value)}
                required={provider.required_config.includes(field)}
                type={field.includes('key') || field.includes('secret') || field.includes('password') ? 'password' : 'text'}
                helperText={provider.required_config.includes(field) ? t('textExtractionConfig.form.required') : t('textExtractionConfig.form.optional')}
              />
            </Grid>
          ))}
        </Grid>
      </SectionCard>
    );
  };

  const renderConfiguration = () => {
    const o = (key: string) => guide(`ocrFields.${key}`);
    const h = (key: string) => guide(`help.${key}`);
    return (
      <Stack spacing={2.5}>
        <SectionCard icon={<CloudIcon fontSize="small" />} title={t('textExtractionConfig.sections.provider.title')} description={t('textExtractionConfig.sections.provider.description')}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>{t('textExtractionConfig.form.provider')}</InputLabel>
                <Select
                  value={selectedProvider}
                  label={t('textExtractionConfig.form.provider')}
                  onChange={(e) => handleProviderChange(e.target.value)}
                  endAdornment={tipAdornment(t('textExtractionConfig.form.provider'), h('provider'))}
                  sx={{ '& .MuiSelect-icon': { right: 40 } }}
                >
                  {providerList.length > 0 ? providerList.map((provider) => (
                    <MenuItem key={provider.value} value={provider.value}>{providerName(provider.value, provider.label)}</MenuItem>
                  )) : (
                    <MenuItem disabled>{isLoadingProviders ? t('textExtractionConfig.messages.loadingProviders') : t('textExtractionConfig.messages.noProvidersAvailable')}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                required
                label={t('textExtractionConfig.form.displayName')}
                value={config.display_name}
                onChange={(e) => handleConfigChange('display_name', e.target.value)}
                slotProps={{ input: { endAdornment: tipAdornment(t('textExtractionConfig.form.displayName'), h('displayName')) } }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label={t('textExtractionConfig.form.description')}
                value={config.description}
                onChange={(e) => handleConfigChange('description', e.target.value)}
                slotProps={{ input: { endAdornment: tipAdornment(t('textExtractionConfig.form.description'), h('description')) } }}
              />
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard icon={<SpeedIcon fontSize="small" />} title={t('textExtractionConfig.sections.general.title')} description={t('textExtractionConfig.sections.general.description')}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              {numberField('timeout', t('textExtractionConfig.form.timeout'), h('timeout'), { step: 10, min: 5, max: 3600 })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {numberField('max_retries', t('textExtractionConfig.form.maxRetries'), h('maxRetries'), { step: 1, min: 0, max: 10 })}
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard icon={<ScanIcon fontSize="small" />} title={t('textExtractionConfig.sections.ocr.title')} description={t('textExtractionConfig.sections.ocr.description')}>
          <Grid container spacing={2}>
            <Grid size={12}>
              {switchField('ocr_enabled', t('textExtractionConfig.form.ocrEnabled'), h('ocrEnabled'))}
            </Grid>
            {config.ocr_enabled && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label={t('textExtractionConfig.form.ocrLanguage')}
                    value={config.ocr_language}
                    onChange={(e) => handleConfigChange('ocr_language', e.target.value)}
                    helperText={compactHint(h('ocrLanguage'))}
                    slotProps={{ input: { endAdornment: tipAdornment(t('textExtractionConfig.form.ocrLanguage'), h('ocrLanguage')) } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t('textExtractionConfig.ocr.engine')}</InputLabel>
                    <Select
                      value={config.ocr_engine}
                      label={t('textExtractionConfig.ocr.engine')}
                      onChange={(e) => handleConfigChange('ocr_engine', e.target.value as OcrSettings['ocr_engine'])}
                      endAdornment={tipAdornment(t('textExtractionConfig.ocr.engine'), o('engine'))}
                      sx={{ '& .MuiSelect-icon': { right: 40 } }}
                    >
                      <MenuItem value="rapidocr">{t('textExtractionConfig.ocr.engineRapidocr')}</MenuItem>
                      <MenuItem value="easyocr">{t('textExtractionConfig.ocr.engineEasyocr')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={12}>
                  <SectionCard tone="subtle" icon={<ScanIcon fontSize="small" />} title={t('textExtractionConfig.sections.ocrDetection.title')} description={t('textExtractionConfig.sections.ocrDetection.description')}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>{switchField('ocr_page_detection_enabled', t('textExtractionConfig.ocr.pageDetection'), o('pageDetection'))}</Grid>
                      <Grid size={{ xs: 12, md: 6 }}>{switchField('ocr_language_auto', t('textExtractionConfig.ocr.languageAuto'), o('languageAuto'))}</Grid>
                      <Grid size={{ xs: 12, md: 6 }}>{switchField('ocr_deskew_enabled', t('textExtractionConfig.ocr.deskew'), o('deskew'))}</Grid>
                      <Grid size={{ xs: 12, md: 6 }}>{switchField('ocr_orientation_enabled', t('textExtractionConfig.ocr.orientation'), o('orientation'))}</Grid>
                      <Grid size={{ xs: 12, md: 6 }}>{numberField('ocr_page_min_chars', t('textExtractionConfig.ocr.pageMinChars'), o('pageMinChars'), { step: 1, min: 0, max: 5000 })}</Grid>
                      <Grid size={{ xs: 12, md: 6 }}>{numberField('ocr_page_min_image_coverage', t('textExtractionConfig.ocr.pageMinCoverage'), o('pageMinCoverage'), { step: 0.05, min: 0, max: 1 })}</Grid>
                      <Grid size={{ xs: 12, md: 6 }}>{numberField('ocr_deskew_min_angle', t('textExtractionConfig.ocr.deskewMinAngle'), o('deskewMinAngle'), { step: 0.1, min: 0.1, max: 10 })}</Grid>
                      <Grid size={{ xs: 12, md: 6 }}>{numberField('ocr_max_pages', t('textExtractionConfig.ocr.maxPages'), o('maxPages'), { step: 10, min: 0, max: 100000 })}</Grid>
                    </Grid>
                  </SectionCard>
                </Grid>

                <Grid size={12}>
                  <SectionCard tone="subtle" icon={<QualityIcon fontSize="small" />} title={t('textExtractionConfig.sections.ocrQuality.title')} description={t('textExtractionConfig.sections.ocrQuality.description')}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>{switchField('ocr_noise_filter_enabled', t('textExtractionConfig.ocr.noiseFilter'), o('noiseFilter'))}</Grid>
                      <Grid size={{ xs: 12, md: 6 }}>{switchField('ocr_dictionary_correction_enabled', t('textExtractionConfig.ocr.dictionaryCorrection'), o('dictionaryCorrection'))}</Grid>
                      <Grid size={{ xs: 12, md: 6 }}>{switchField('ocr_second_pass_enabled', t('textExtractionConfig.ocr.secondPass'), o('secondPass'))}</Grid>
                      <Grid size={{ xs: 12, md: 6 }}>{numberField('ocr_second_pass_min_p10', t('textExtractionConfig.ocr.secondPassMinP10'), o('secondPassMinP10'), { step: 0.05, min: 0, max: 1 })}</Grid>
                      <Grid size={12}>
                        <TextField
                          fullWidth
                          label={t('textExtractionConfig.ocr.vocabularyPath')}
                          value={config.ocr_vocabulary_path}
                          onChange={(e) => handleConfigChange('ocr_vocabulary_path', e.target.value)}
                          helperText={compactHint(o('vocabularyPath'))}
                          slotProps={{ input: { endAdornment: tipAdornment(t('textExtractionConfig.ocr.vocabularyPath'), o('vocabularyPath')) } }}
                        />
                      </Grid>
                    </Grid>
                  </SectionCard>
                </Grid>

                <Grid size={12}>
                  <SectionCard tone="subtle" icon={<SpeedIcon fontSize="small" />} title={t('textExtractionConfig.sections.ocrPerformance.title')} description={t('textExtractionConfig.sections.ocrPerformance.description')}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>{numberField('ocr_render_scale', t('textExtractionConfig.ocr.renderScale'), o('renderScale'), { step: 0.5, min: 1, max: 6 })}</Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControl fullWidth>
                          <InputLabel>{t('textExtractionConfig.ocr.onnxDevice')}</InputLabel>
                          <Select
                            value={config.ocr_onnx_device}
                            label={t('textExtractionConfig.ocr.onnxDevice')}
                            onChange={(e) => handleConfigChange('ocr_onnx_device', e.target.value as OcrSettings['ocr_onnx_device'])}
                            endAdornment={tipAdornment(t('textExtractionConfig.ocr.onnxDevice'), o('onnxDevice'))}
                            sx={{ '& .MuiSelect-icon': { right: 40 } }}
                          >
                            <MenuItem value="cpu">CPU</MenuItem>
                            <MenuItem value="auto">{t('textExtractionConfig.ocr.onnxDeviceAuto')}</MenuItem>
                            <MenuItem value="cuda">CUDA</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </SectionCard>
                </Grid>
              </>
            )}
          </Grid>
        </SectionCard>

        <SectionCard icon={<TableIcon fontSize="small" />} title={t('textExtractionConfig.sections.extraction.title')} description={t('textExtractionConfig.sections.extraction.description')}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>{switchField('extract_tables', t('textExtractionConfig.form.extractTables'), h('extractTables'))}</Grid>
            <Grid size={{ xs: 12, md: 4 }}>{switchField('extract_images', t('textExtractionConfig.form.extractImages'), h('extractImages'))}</Grid>
            <Grid size={{ xs: 12, md: 4 }}>{switchField('preserve_layout', t('textExtractionConfig.form.preserveLayout'), h('preserveLayout'))}</Grid>
          </Grid>
        </SectionCard>

        {renderProviderConfigFields()}

        <Paper
          variant="outlined"
          sx={{ position: 'sticky', bottom: 12, zIndex: 2, p: 1.5, borderRadius: 3, display: 'flex', gap: 1.5, justifyContent: 'flex-end', alignItems: 'center', backdropFilter: 'blur(6px)', backgroundColor: 'background.paper' }}
        >
          <Tooltip arrow title={t('textExtractionConfig.actions.applyRecommendedHint')}>
            <span>
              <Button variant="outlined" startIcon={<RecommendIcon />} onClick={applyRecommended}>
                {t('textExtractionConfig.actions.applyRecommended')}
              </Button>
            </span>
          </Tooltip>
          <Tooltip arrow title={t('textExtractionConfig.actions.testHint')}>
            <span>
              <Button variant="outlined" startIcon={<TestIcon />} onClick={() => { void testConfiguration(); }} disabled={testConfig.isPending || !selectedProvider}>
                {testConfig.isPending ? t('textExtractionConfig.messages.testing') : t('textExtractionConfig.actions.test')}
              </Button>
            </span>
          </Tooltip>
          <Tooltip arrow title={t('textExtractionConfig.actions.saveHint')}>
            <span>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={() => { void saveConfiguration(); }} disabled={saveConfig.isPending || !selectedProvider || !config.display_name}>
                {t('textExtractionConfig.actions.saveAndActivate')}
              </Button>
            </span>
          </Tooltip>
        </Paper>

        {testResult && (
          <Alert severity={testResult.success ? 'success' : 'error'} sx={{ borderRadius: 2 }}>
            <Typography variant="body2">{testResult.message}</Typography>
            {testResult.error && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>{t('textExtractionConfig.testResult.errorDetails')}: {testResult.error}</Typography>
            )}
            {testResult.success && (
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mt: 1 }}>
                <Chip size="small" color={testResult.health ? 'success' : 'error'} label={`${t('textExtractionConfig.testResult.health')}: ${testResult.health ? t('textExtractionConfig.testResult.healthy') : t('textExtractionConfig.testResult.unhealthy')}`} />
                {testResult.supported_types.map((type) => (
                  <Chip key={type} size="small" variant="outlined" label={type.toUpperCase()} />
                ))}
              </Stack>
            )}
          </Alert>
        )}
      </Stack>
    );
  };

  const tabs: AdminTab[] = [
    { label: t('textExtractionConfig.tabs.currentConfiguration'), icon: <SettingsIcon />, content: renderCurrentConfig() },
    { label: t('textExtractionConfig.tabs.allConfigurations'), icon: <ListIcon />, content: renderAllConfigs() },
    { label: t('textExtractionConfig.tabs.availableProviders'), icon: <CloudIcon />, content: renderAvailableProviders() },
    { label: t('textExtractionConfig.tabs.createConfiguration'), icon: <AddIcon />, content: renderConfiguration() },
  ];

  return (
    <>
      <PageLayout title={t('textExtractionConfig.title')} isLoading={isLoading} maxWidth="xl">
        <AdminTabs tabs={tabs} value={activeTab} onChange={setActiveTab} variant="standard" />
      </PageLayout>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('textExtractionConfig.dialog.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('textExtractionConfig.dialog.deleteMessage')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('textExtractionConfig.dialog.cancel')}</Button>
          <Button onClick={() => { if (configToDelete) { void deleteConfiguration(configToDelete); } }} color="error" disabled={deleteConfig.isPending}>
            {t('textExtractionConfig.dialog.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TextExtractionConfig;
